import React, { useState } from 'react';
import { Demande } from '../../models/demandeAdmin';
import { UserModel } from '../../models/UserModel';
import { format } from 'date-fns';
import '../../page/style/style.css';
import { safeParseContent } from '../../utils/functions/safeParseContent';
import { toast } from 'react-toastify';
import { Spin, Modal, Input } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const { TextArea } = Input;

interface DetailDemandeProps {
  demande: Demande;
  user: UserModel;
  onValidate?: (demandeId: number) => Promise<void>;
  onReject?: (demandeId: number, reason: string) => Promise<void>;
}

interface ModifiedField {
  key: string;
  label: string;
  icon: string;
  contentValue: string;
  userValue: string;
}

interface ModifiedDocument {
  key: string;
  label: string;
  icon: string;
  newUrl: string | null;
  currentUrl: string | null;
}

const DetailDemande: React.FC<DetailDemandeProps> = ({ demande, user, onValidate, onReject }) => {

  const [isValidating, setIsValidating] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Parse le content et corrige la double imbrication si elle existe
  let content = safeParseContent(demande.content);

  // FIX: Corriger la double imbrication du content
  if (content && content.content && typeof content.content === 'object') {
    content = content.content;
  }

  const isProfessionalRequest =
    (demande.typeDemande && demande.typeDemande.toUpperCase() === 'PROFESSIONAL') ||
    content?.request_type === 'professional' ||
    content?.request_type === 'professional_account' ||
    (typeof content?.requestType === 'string' && content.requestType.toLowerCase().includes('professional'));

  const isFieldModified = (contentValue: any, userValue: any): boolean => {
    const normalizeValue = (value: any): string => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (value instanceof Date) return value.toISOString();
      try { return JSON.stringify(value); } catch { return String(value); }
    };

    const normalizedContent = normalizeValue(contentValue);
    const normalizedUser = normalizeValue(userValue);
    return normalizedContent !== normalizedUser;
  };

  const standardFields = [
    { key: 'name', label: 'Nom', icon: 'bi-person-vcard' },
    { key: 'firstname', label: 'Prénom', icon: 'bi-person' },
    { key: 'email', label: 'Email', icon: 'bi-envelope-at' },
    { key: 'adresse', label: 'Adresse', icon: 'bi-geo-alt' },
    { key: 'number', label: 'Numéro', icon: 'bi-telephone' },
    { key: 'postal_code', label: 'Code postal', icon: 'bi-mailbox' },
    { key: 'country', label: 'Ville', icon: 'bi-flag' },
    { key: 'civility', label: 'Civilité', icon: 'bi-gender-ambiguous' },
    { key: 'raison_social', label: 'Raison sociale', icon: 'bi-building' },
    { key: 'code_NAF', label: 'CODE NAF', icon: 'bi-briefcase' },
    { key: 'code_SIREN', label: 'CODE SIREN', icon: 'bi-123' },
    { key: 'tv_intra', label: 'N° TVA', icon: 'bi-receipt' },
    { key: 'profession', label: 'Profession', icon: 'bi-person-badge' },
    { key: 'rib', label: 'RIB / IBAN', icon: 'bi-credit-card' },
    { key: 'account_number', label: 'Numéro de compte', icon: 'bi-credit-card' },
    { key: 'bic', label: 'BIC / SWIFT', icon: 'bi-bank' },
    { key: 'bic_swift', label: 'BIC / SWIFT', icon: 'bi-bank' },
    // ✅ AJOUT : show_in_directory
    { key: 'show_in_directory', label: 'Apparaître dans l\'annuaire', icon: 'bi-eye' },
  ];

  const getModifiedFields = (): ModifiedField[] => {
    const modifiedFields: ModifiedField[] = [];

    standardFields
      .filter(field => Object.prototype.hasOwnProperty.call(content, field.key))
      .forEach(field => {
        const contentValue = (content as any)[field.key];
        let userValue = (user as any)[field.key];

        if (field.key === 'profession') {
          userValue = user.profession || (user as any).professionType || '';
        }

        if (field.key === 'rib' && !userValue) {
          userValue = user.account_number || '';
        }

        if (field.key === 'bic' && !userValue) {
          userValue = user.bic_swift || '';
        }

        // ✅ Gestion spéciale pour show_in_directory (boolean)
        if (field.key === 'show_in_directory') {
          // Convertir les valeurs en booléens pour la comparaison
          const contentBool = contentValue === true || contentValue === 'true';
          const userBool = userValue === true || userValue === 'true';
          
          if (contentBool !== userBool) {
            modifiedFields.push({
              key: String(field.key),
              label: field.label,
              icon: field.icon,
              contentValue: contentBool ? 'Oui' : 'Non',
              userValue: userBool ? 'Oui' : 'Non',
            });
          }
          return; // Ne pas continuer avec la comparaison standard
        }

        if (isFieldModified(contentValue, userValue)) {
          modifiedFields.push({
            key: String(field.key),
            label: field.label,
            icon: field.icon,
            contentValue: String(contentValue ?? ''),
            userValue: String(userValue ?? ''),
          });
        }
      });

    return modifiedFields;
  };

  const modifiedFields = getModifiedFields();

  const resolveDocumentUrl = (path?: string | null): string | null => {
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) return path;

    let normalized = path.replace(/\\/g, '/');

    if (!normalized.startsWith('storage/')) {
      normalized = `storage/${normalized}`;
    }

    const base = process.env.REACT_APP_API_BASE?.replace(/\/$/, '') || 'http://localhost:5200';

    return `${base}/${normalized}`;
  };

  const getDocumentUrl = (contentKeys: string[], userKey: keyof UserModel): string | null => {
    // D'abord chercher dans content
    for (const key of contentKeys) {
      if (content[key]) {
        const resolved = resolveDocumentUrl(content[key]);
        if (resolved) {
          return resolved;
        }
      }
    }
    // Sinon chercher dans user
    const userValue = user[userKey] as string;
    if (userValue) {
      const resolved = resolveDocumentUrl(userValue);
      return resolved;
    }
    return null;
  };

  // Fonction pour détecter les documents modifiés
  const getModifiedDocuments = (): ModifiedDocument[] => {
    const modifiedDocs: ModifiedDocument[] = [];

    const documentMappings = [
      {
        key: 'kbis_document',
        label: 'Extrait Kbis',
        icon: 'bi-file-earmark-pdf',
        contentKeys: ['kbisUrl', 'kbis', 'kbis_path', 'kbis_url', 'kbis_document'],
        userKey: 'kbis_document' as keyof UserModel
      },
      {
        key: 'identity_document_recto',
        label: 'Pièce d\'identité - Recto',
        icon: 'bi-person-badge',
        contentKeys: ['identityRectoUrl', 'identity_recto', 'identity_recto_url', 'identityRecto', 'identity_document_recto'],
        userKey: 'identity_document_recto' as keyof UserModel
      },
      {
        key: 'identity_document_verso',
        label: 'Pièce d\'identité - Verso',
        icon: 'bi-person-badge',
        contentKeys: ['identityVersoUrl', 'identity_verso', 'identity_verso_url', 'identityVerso', 'identity_document_verso'],
        userKey: 'identity_document_verso' as keyof UserModel
      },
      {
        key: 'rib_document',
        label: 'Document RIB',
        icon: 'bi-bank',
        contentKeys: ['ribUrl', 'rib_document', 'rib_document_url'],
        userKey: 'rib_document' as keyof UserModel
      }
    ];

    documentMappings.forEach(doc => {
      let newUrl: string | null = null;
      
      // Chercher dans content
      for (const key of doc.contentKeys) {
        if (content[key]) {
          newUrl = resolveDocumentUrl(content[key]);
          if (newUrl) break;
        }
      }

      const currentUrl = resolveDocumentUrl((user[doc.userKey] as string) || null);

      // Si un nouveau document existe ou si c'est différent de l'ancien
      if (newUrl && newUrl !== currentUrl) {
        modifiedDocs.push({
          key: doc.key,
          label: doc.label,
          icon: doc.icon,
          newUrl,
          currentUrl
        });
      }
    });

    return modifiedDocs;
  };

  const modifiedDocuments = getModifiedDocuments();

  const kbisUrl = getDocumentUrl(
    ['kbisUrl', 'kbis', 'kbis_path', 'kbis_url', 'kbis_document'],
    'kbis_document'
  );

  const identityRectoUrl = getDocumentUrl(
    ['identityRectoUrl', 'identity_recto', 'identity_recto_url', 'identityRecto', 'identity_document_recto'],
    'identity_document_recto'
  );

  const identityVersoUrl = getDocumentUrl(
    ['identityVersoUrl', 'identity_verso', 'identity_verso_url', 'identityVerso', 'identity_document_verso'],
    'identity_document_verso'
  );

  const ribDocumentUrl = getDocumentUrl(
    ['ribUrl', 'rib_document', 'rib_document_url'],
    'rib_document'
  );

  const rib = content.rib || user.account_number || null;
  const bic = content.bic || user.bic_swift || null;

  const rawIdentityType = content.identity_type ?? content.identityType ?? content.identity ?? '';
  const identityTypeNormalized = typeof rawIdentityType === 'string' ? rawIdentityType.trim().toLowerCase() : '';
  const isPassport = /pass|passeport|passport/i.test(identityTypeNormalized);

  const resolveImageUrl = (path: string | null | undefined): string | null => {
    return resolveDocumentUrl(path);
  };

  const demandeProfilePicturePath = content.profile_picture as string | undefined;
  const userProfilePicturePath = user.profile_picture as string | undefined;

  const newProfilePictureUrl = resolveImageUrl(demandeProfilePicturePath);
  const currentProfilePictureUrl = resolveImageUrl(userProfilePicturePath);

  const openDocument = (url: string | null, label: string) => {
    if (!url) {
      toast.error(`Document ${label} non disponible`);
      return;
    }

    if (!/^https?:\/\//i.test(url)) {
      toast.error(`URL invalide pour ${label}`);
      console.error('URL invalide:', url);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatRIB = (rib: string) => {
    const clean = rib.replace(/\s+/g, "");
    if (clean.length !== 23) return rib;
    const bank = clean.slice(0, 5);
    const guichet = clean.slice(5, 10);
    const compte = clean.slice(10, 21);
    const cle = clean.slice(21);
    return `${bank} ${guichet} ${compte} ${cle}`;
  };

  // Fonction pour afficher les documents modifiés
  const renderModifiedDocumentsSection = () => {
    if (isProfessionalRequest || modifiedDocuments.length === 0) return null;

    return (
      <div className="modified-documents-section" style={{ marginTop: '1.5rem' }}>
        <div className="section-header">
          <i className="bi bi-files"></i>
          <h3>Documents modifiés ({modifiedDocuments.length})</h3>
        </div>

        <div className="documents-grid">
          {modifiedDocuments.map((doc) => (
            <div key={doc.key} className="document-card modified">
              <div className="document-header">
                <i className={`${doc.icon} document-icon`}></i>
                <div className="document-info">
                  <h4>{doc.label}</h4>
                  <span className="modification-badge">
                    <i className="bi bi-arrow-repeat"></i>
                    Document modifié
                  </span>
                </div>
              </div>

              <div className="document-comparison">
                {doc.currentUrl && (
                  <div className="document-version current">
                    <div className="version-label">
                      <i className="bi bi-check-circle"></i>
                      <span>Actuel</span>
                    </div>
                    <button
                      className="btn-view-document-small"
                      onClick={() => openDocument(doc.currentUrl, `${doc.label} (actuel)`)}
                    >
                      <i className="bi bi-eye"></i>
                      Voir l'actuel
                    </button>
                  </div>
                )}

                <div className="document-version new">
                  <div className="version-label new">
                    <i className="bi bi-arrow-right-circle"></i>
                    <span>Nouveau</span>
                  </div>
                  <button
                    className="btn-view-document-small new"
                    onClick={() => openDocument(doc.newUrl, `${doc.label} (nouveau)`)}
                  >
                    <i className="bi bi-eye"></i>
                    Voir le nouveau
                  </button>
                </div>
              </div>

              {doc.currentUrl && doc.newUrl && (
                <button
                  className="btn-compare-docs"
                  onClick={() => {
                    window.open(doc.currentUrl!, '_blank');
                    setTimeout(() => window.open(doc.newUrl!, '_blank'), 100);
                  }}
                  style={{ marginTop: '0.5rem', width: '100%' }}
                >
                  <i className="bi bi-window-stack"></i>
                  Comparer côte à côte
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDocumentsSection = () => {
    if (!isProfessionalRequest) return null;

    return (
      <div className="documents-section">
        <div className="section-header">
          <i className="bi bi-folder2-open"></i>
          <h3>Documents fournis pour le compte professionnel</h3>
        </div>

        <div className="documents-grid">
          {/* Extrait Kbis */}
          <div className="document-card">
            <div className="document-header">
              <i className="bi bi-file-earmark-pdf document-icon kbis"></i>
              <div className="document-info">
                <h4>Extrait Kbis</h4>
                <p className="document-description">
                  Document légal justifiant l'existence de l'entreprise
                </p>
              </div>
            </div>
            <div className="document-actions">
              {kbisUrl ? (
                <button
                  className="btn-view-document kbis"
                  onClick={() => openDocument(kbisUrl, "Kbis")}
                >
                  <i className="bi bi-eye me-2"></i>
                  Voir le document
                </button>
              ) : (
                <div className="no-document">
                  <i className="bi bi-file-x"></i>
                  <span>Document non fourni</span>
                </div>
              )}
            </div>
          </div>

          {/* Pièce d'identité Recto */}
          <div className="document-card">
            <div className="document-header">
              <i className="bi bi-person-badge document-icon identity"></i>
              <div className="document-info">
                <h4>Pièce d'identité - Recto</h4>
                <p className="document-description">
                  {isPassport ? "Passeport" : "Carte d'Identité Nationale"}
                </p>
              </div>
            </div>
            <div className="document-actions">
              {identityRectoUrl ? (
                <button
                  className="btn-view-document identity"
                  onClick={() => openDocument(identityRectoUrl, "Recto")}
                >
                  <i className="bi bi-eye me-2"></i>
                  Voir le document
                </button>
              ) : (
                <div className="no-document">
                  <i className="bi bi-file-x"></i>
                  <span>Document non fourni</span>
                </div>
              )}
            </div>
          </div>

          {/* Pièce d'identité Verso (si CIN) */}
          {!isPassport && (
            <div className="document-card">
              <div className="document-header">
                <i className="bi bi-person-badge document-icon identity"></i>
                <div className="document-info">
                  <h4>Pièce d'identité - Verso</h4>
                  <p className="document-description">
                    Verso de la Carte d'Identité Nationale
                  </p>
                </div>
              </div>
              <div className="document-actions">
                {identityVersoUrl ? (
                  <button
                    className="btn-view-document identity"
                    onClick={() => openDocument(identityVersoUrl, "Verso")}
                  >
                    <i className="bi bi-eye me-2"></i>
                    Voir le document
                  </button>
                ) : (
                  <div className="no-document">
                    <i className="bi bi-file-x"></i>
                    <span>Document non fourni</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Document RIB */}
          <div className="document-card">
            <div className="document-header">
              <i className="bi bi-bank document-icon rib"></i>
              <div className="document-info">
                <h4>Relevé d'Identité Bancaire (RIB)</h4>
                <p className="document-description">
                  Justificatif bancaire pour remboursements
                </p>
              </div>
            </div>
            <div className="document-actions">
              {ribDocumentUrl ? (
                <button
                  className="btn-view-document rib"
                  onClick={() => openDocument(ribDocumentUrl, "RIB")}
                >
                  <i className="bi bi-eye me-2"></i>
                  Voir le document
                </button>
              ) : (
                <div className="no-document">
                  <i className="bi bi-file-x"></i>
                  <span>Document non fourni</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coordonnées bancaires texte */}
        {(rib || bic) && (
          <div className="info-section banking-section" style={{ marginTop: '1.5rem' }}>
            <div className="section-header">
              <i className="bi bi-bank2"></i>
              <h3>Coordonnées bancaires saisies</h3>
            </div>
            <div className="banking-info-box">
              <div className="info-grid">
                {rib && (
                  <div className="info-item banking-item">
                    <div className="info-label">
                      <i className="bi bi-credit-card-2-front me-2"></i>
                      RIB (23 caractères)
                    </div>
                    <div className="info-value banking-value">
                      {formatRIB(rib)}
                    </div>
                  </div>
                )}
                {bic && (
                  <div className="info-item banking-item">
                    <div className="info-label">
                      <i className="bi bi-bank me-2"></i>
                      BIC/SWIFT
                    </div>
                    <div className="info-value banking-value">
                      {bic}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Vérification des coordonnées bancaires */}
              <div className="banking-status mt-3">
                {rib && bic ? (
                  <div className="status-complete">
                    <i className="bi bi-check-circle-fill"></i>
                    <span>Coordonnées bancaires complètes</span>
                  </div>
                ) : (
                  <div className="status-incomplete">
                    <i className="bi bi-exclamation-triangle-fill"></i>
                    <span>Coordonnées bancaires incomplètes</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Guide de vérification */}
        <div className="verification-guide" style={{ marginTop: '1.5rem' }}>
          <div className="guide-header">
            <i className="bi bi-info-circle"></i>
            <h4>Guide de vérification</h4>
          </div>
          <ul className="guide-list">
            <li><i className="bi bi-check-circle"></i> Kbis datant de moins de 3 mois</li>
            <li><i className="bi bi-check-circle"></i> Pièce d'identité en cours de validité</li>
            <li><i className="bi bi-check-circle"></i> Document RIB lisible et complet</li>
            <li><i className="bi bi-check-circle"></i> Correspondance RIB document vs RIB saisi</li>
            <li><i className="bi bi-check-circle"></i> Correspondance des informations (SIREN, nom, BIC)</li>
            <li><i className="bi bi-check-circle"></i> Documents lisibles et complets</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderProfessionalInfo = () => {
    if (!isProfessionalRequest) return null;

    return (
      <div className="professional-info-card">
        <div className="professional-info-header">
          <i className="bi bi-briefcase-fill"></i>
          <h3>Demande de compte professionnel</h3>
        </div>
        {!isProfessionalRequest && (newProfilePictureUrl || currentProfilePictureUrl) && (
          <div className="profile-picture-section">
            <div className="section-header">
              <i className="bi bi-images"></i>
              <h3>Photo de profil</h3>
              <span className="section-subtitle">
                {currentProfilePictureUrl && newProfilePictureUrl 
                  ? "Comparaison des versions" 
                  : currentProfilePictureUrl 
                    ? "Version actuelle" 
                    : "Nouvelle version"}
              </span>
            </div>
            
            <div className="profile-picture-carousel">
              {currentProfilePictureUrl && (
                <div className="profile-slide">
                  <div className="slide-header">
                    <span className="slide-title">Actuelle</span>
                    <span className="slide-badge">En cours</span>
                  </div>
                  <div className="slide-image-container">
                    <img
                      src={currentProfilePictureUrl}
                      alt="Photo actuelle"
                      className="slide-image"
                    />
                    <div className="slide-overlay">
                      <button
                        className="btn-fullscreen"
                        onClick={() => window.open(currentProfilePictureUrl!, '_blank')}
                      >
                        <i className="bi bi-arrows-fullscreen"></i> Plein écran
                      </button>
                    </div>
                  </div>
                  <div className="slide-info">
                    <i className="bi bi-info-circle"></i>
                    <span>Cliquez sur l'image pour l'agrandir</span>
                  </div>
                </div>
              )}
              
              {newProfilePictureUrl && (
                <div className="profile-slide">
                  <div className="slide-header">
                    <span className="slide-title">Nouvelle</span>
                    <span className="slide-badge badge-new">Demandée</span>
                  </div>
                  <div className="slide-image-container">
                    <img
                      src={newProfilePictureUrl}
                      alt="Nouvelle photo"
                      className="slide-image"
                    />
                    <div className="slide-overlay">
                      <button
                        className="btn-fullscreen"
                        onClick={() => window.open(newProfilePictureUrl!, '_blank')}
                      >
                        <i className="bi bi-arrows-fullscreen"></i> Plein écran
                      </button>
                    </div>
                  </div>
                  <div className="slide-info">
                    <i className="bi bi-arrow-right-circle"></i>
                    <span>Cette photo remplacera la version actuelle</span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="profile-actions">
              <button
                className="btn-compare-images"
                onClick={() => {
                  if (currentProfilePictureUrl && newProfilePictureUrl) {
                    window.open(currentProfilePictureUrl!, '_blank');
                    setTimeout(() => window.open(newProfilePictureUrl!, '_blank'), 100);
                  }
                }}
                disabled={!currentProfilePictureUrl || !newProfilePictureUrl}
              >
                <i className="bi bi-window-stack"></i>
                Ouvrir les deux côte à côte
              </button>
            </div>
          </div>
        )}
        <div className="user-info-grid">
          <div className="info-item">
            <span className="info-label">Nom</span>
            <span className="info-value">{user.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Prénom</span>
            <span className="info-value">{user.firstname}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Profession</span>
            <span className="info-value">{user.profession || (user as any).professionType || 'Non spécifiée'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Civilité</span>
            <span className="info-value">{user.civility === 'monsieur' ? 'Monsieur' : 'Madame'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email</span>
            <span className="info-value">{user.email}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Adresse</span>
            <span className="info-value">{user.adresse}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Numéro</span>
            <span className="info-value">{user.number}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Code postal</span>
            <span className="info-value">{user.postal_code}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Ville</span>
            <span className="info-value">{user.country}</span>
          </div>
          <div className="info-item full-width">
            <span className="info-label">Type de compte actuel</span>
            <span className={`account-badge ${user.user_type}`}>
              {user.user_type === 'personne' ? 'Particulier' :
                user.user_type === 'entreprise' ? 'Entreprise' : 'Professionnel'}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const handleValidateClick = async () => {
    if (!onValidate) return;

    setIsValidating(true);
    try {
      await onValidate(demande.id);

      console.log("✅ Demande validée avec succès");
      
    } catch (error) {
      console.error('Erreur validation:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const showRejectModal = () => {
    setIsRejectModalVisible(true);
    setRejectReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Le motif du rejet est obligatoire');
      return;
    }

    if (!onReject) return;

    setIsRejecting(true);
    try {
      await onReject(demande.id, rejectReason);
      setIsRejectModalVisible(false);
      setRejectReason('');
      toast.success('Demande rejetée avec succès');
    } catch (error) {
      console.error('Erreur rejet:', error);
      toast.error('Erreur lors du rejet de la demande');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRejectCancel = () => {
    if (isRejecting) return;
    setIsRejectModalVisible(false);
    setRejectReason('');
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24, color: '#4bb0c0' }} spin />;

  const RenderContent: React.FC = () => (
    <div className="detail-demande-container">
      {renderProfessionalInfo()}
      {renderDocumentsSection()}

      {!isProfessionalRequest && (
        <div className="modifications-section">
          <div className="user-info-summary">
            <i className="bi bi-person-circle"></i>
            <span><strong>{user.firstname} {user.name}</strong> — ID: {user.id}</span>
          </div>

          {/* ✅ Photo de profil modifiée */}
          {!isProfessionalRequest && (newProfilePictureUrl || currentProfilePictureUrl) && (
            <div className="profile-picture-section">
              <div className="section-header">
                <i className="bi bi-image"></i>
                <h3>Photo de profil</h3>
              </div>
              
              <div className="profile-picture-compare">
                {currentProfilePictureUrl && (
                  <div className="profile-picture-block current">
                    <div className="profile-picture-header">
                      <i className="bi bi-check-circle-fill"></i>
                      <span className="info-label">Actuelle</span>
                    </div>
                    <div className="profile-picture-wrapper">
                      <img
                        src={currentProfilePictureUrl}
                        alt="Photo de profil actuelle"
                        className="profile-picture-img"
                        onClick={() => window.open(currentProfilePictureUrl!, '_blank')}
                        title="Cliquez pour agrandir"
                      />
                    </div>
                    {!newProfilePictureUrl && (
                      <div className="profile-picture-status unchanged">
                        <i className="bi bi-dash-circle"></i>
                        <span>Non modifiée</span>
                      </div>
                    )}
                  </div>
                )}
                
                {newProfilePictureUrl && (
                  <div className="profile-picture-block new">
                    <div className="profile-picture-header">
                      <i className="bi bi-arrow-right-circle-fill"></i>
                      <span className="info-label">Nouvelle (demandée)</span>
                    </div>
                    <div className="profile-picture-wrapper">
                      <img
                        src={newProfilePictureUrl}
                        alt="Nouvelle photo de profil demandée"
                        className="profile-picture-img"
                        onClick={() => window.open(newProfilePictureUrl!, '_blank')}
                        title="Cliquez pour agrandir"
                      />
                    </div>
                    {currentProfilePictureUrl && (
                      <div className="profile-picture-status changed">
                        <i className="bi bi-arrow-right"></i>
                        <span>Remplacement demandé</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ✅ Documents professionnels modifiés */}
          {renderModifiedDocumentsSection()}

          {/* Champs textuels modifiés */}
          {modifiedFields.length === 0 && modifiedDocuments.length === 0 ? (
            <div className="no-modifications">
              <i className="bi bi-info-circle"></i>
              <span>Aucune modification détectée dans cette demande.</span>
            </div>
          ) : modifiedFields.length > 0 ? (
            <>
              <div className="section-header">
                <h3>Champs modifiés ({modifiedFields.length})</h3>
              </div>
              <div className="modifications-table-container">
                <table className="modifications-table">
                  <thead>
                    <tr>
                      <th>Champ</th>
                      <th>Modification souhaitée</th>
                      {demande.status === 0 && <th>Information actuelle</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {modifiedFields.map((field, i) => (
                      <tr key={field.key + i}>
                        <td className="field-name">
                          <i className={`${field.icon}`} />
                          <span>{field.label}</span>
                        </td>
                        <td className="field-new-value">
                          {field.key === 'rib' || field.key === 'account_number' 
                            ? formatRIB(field.contentValue) 
                            : field.key === 'show_in_directory' ? (
                              <span className={`status-badge ${field.contentValue === 'Oui' ? 'active' : 'inactive'}`}>
                                {field.contentValue === 'Oui' ? (
                                  <>
                                    <i className="bi bi-check-circle" style={{color:"black"}}></i>
                                    <span style={{color:"black"}}>Visible dans l'annuaire</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-eye-slash" style={{color:"black"}}></i>
                                    <span style={{color:"black"}}>Non visible</span>
                                  </>
                                )}
                              </span>
                            ) : (
                              field.contentValue || '(vide)'
                            )}
                        </td>
                        {demande.status === 0 &&
                          <td className="field-old-value">
                            {field.key === 'rib' || field.key === 'account_number' 
                              ? formatRIB(field.userValue) 
                              : field.key === 'show_in_directory' ? (
                                <span className={`status-badge ${field.userValue === 'Oui' ? 'active' : 'inactive'}`}>
                                  {field.userValue === 'Oui' ? (
                                    <>
                                      <i className="bi bi-check-circle" style={{color:"black"}}></i>
                                      <span style={{color:"black"}}>Visible</span>
                                    </>
                                  ) : (
                                    <>
                                    <i className="bi bi-eye-slash" style={{color:"black"}}></i>
                                    <span style={{color:"black"}}>Non visible</span>
                                    </>
                                  )}
                                </span>
                              ) : (
                                field.userValue || '(vide)'
                              )}
                          </td>
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      {demande.status !== 0 && (
        <div className={`status-alert ${demande.status === 3 ? 'validated' : 'rejected'}`}>
          <i className={`bi ${demande.status === 3 ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
          <div className="status-content">
            <span className="status-title">
              Demande {demande.status === 3 ? 'validée' : 'rejetée'}
            </span>
            {content.validatedAt && (
              <span className="status-date">
                le {format(new Date(content.validatedAt), 'dd/MM/yyyy à HH:mm')}
              </span>
            )}
          </div>
        </div>
      )}

      {demande.status === 0 && (onValidate || onReject) && (
        <div className="action-buttons">
          {onValidate && (
            <button
              className="btn-validate"
              onClick={handleValidateClick}
              disabled={isValidating}
            >
              {isValidating ? (
                <Spin indicator={antIcon} />
              ) : (
                <>
                  <i className="bi bi-check-circle"></i>
                  Valider la demande
                </>
              )}
            </button>
          )}
          {onReject && (
            <button
              className="btn-reject"
              onClick={showRejectModal}
            >
              <i className="bi bi-x-circle"></i>
              Rejeter la demande
            </button>
          )}
        </div>
      )}

      {/* Modal de rejet */}
      <Modal
        title={
          <div className="reject-modal-header">
            <i className="bi bi-exclamation-triangle"></i>
            <span>Rejeter la demande</span>
          </div>
        }
        open={isRejectModalVisible}
        onCancel={handleRejectCancel}
        footer={[
          <button
            key="cancel"
            onClick={handleRejectCancel}
            disabled={isRejecting}
            className="btn-cancel-modal"
          >
            Annuler
          </button>,
          <button
            key="confirm"
            onClick={handleRejectConfirm}
            disabled={isRejecting || !rejectReason.trim()}
            className="btn-confirm-reject"
          >
            {isRejecting ? (
              <>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: 'white' }} spin />} />
                <span>Rejet en cours...</span>
              </>
            ) : (
              <>
                <i className="bi bi-x-circle"></i>
                <span>Confirmer le rejet</span>
              </>
            )}
          </button>
        ]}
        width={600}
        maskClosable={!isRejecting}
        closable={!isRejecting}
        className="reject-modal"
      >
        <div className="reject-modal-content">
          <p className="reject-instruction">
            Veuillez indiquer le motif du rejet. Ce message sera envoyé par email à l'utilisateur.
          </p>
          <div className="reject-reason-input">
            <label>
              Motif du rejet <span className="required-star">*</span>
            </label>
            <TextArea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Ex: Documents non conformes, informations manquantes..."
              rows={4}
              maxLength={500}
              showCount
              autoFocus
              className={!rejectReason.trim() ? 'error-border' : ''}
            />
          </div>
          {!rejectReason.trim() && (
            <p className="error-message">
              <i className="bi bi-info-circle me-1"></i>
              Le motif du rejet est obligatoire
            </p>
          )}
        </div>
      </Modal>
    </div>
  );

  return <RenderContent />;
};

export default DetailDemande;